import {
  AnketaAuthUser,
  AnketaCheckPermissionOptions,
  AnketaPermissions,
  AnketaResources,
  AnketaRolesWithPermissions,
} from "../types";
import { ROLES } from "./roles";

/**
 * Organization-attribute flags, read uniformly from Keycloak org attributes
 * (`isAutonubil` / `isMedocino` = string "true"). This is the single definition of
 * "internal" used by the policy engine - it replaces the older
 * `org.alias === "autonubil-gmbh"` test so questionnaires, events and opsanio all
 * agree (AUTH_RECOMMENDATION.md §3). `isInternal = isMedocino || isAutonubil`.
 */
export const getOrgAttributeFlags = (
  user: AnketaAuthUser
): { isAutonubil: boolean; isMedocino: boolean; isInternal: boolean } => {
  const orgAttributes: Record<string, string[]> = (user?.organization ?? [])
    .map((org) => org.attributes)
    .filter(Boolean)
    .reduce((acc, attrs) => ({ ...acc, ...(attrs as any) }), {} as Record<string, string[]>);

  const attrIsTrue = (key: string) =>
    Array.isArray(orgAttributes[key]) && orgAttributes[key][0] === "true";

  const isAutonubil = attrIsTrue("isAutonubil");
  const isMedocino = attrIsTrue("isMedocino");
  return { isAutonubil, isMedocino, isInternal: isMedocino || isAutonubil };
};

export function hasPermission<Resource extends keyof AnketaPermissions>(
  user: AnketaAuthUser,
  resource: Resource,
  action: AnketaPermissions[Resource]["action"],
  options?: AnketaCheckPermissionOptions<AnketaResources>,
  // The role=>permission matrix to evaluate against. Defaults to the hardcoded
  // `ROLES` table; the backend passes the runtime-editable matrix loaded from the
  // database (see functions/auth/permissionMatrix.ts) so the API enforces live
  // permissions, and the frontend passes the matrix fetched from the server so UI
  // affordances match. Either way `ROLES` is the fail-safe fallback.
  roles: AnketaRolesWithPermissions = ROLES as AnketaRolesWithPermissions
): boolean {
  if (!user || !user.roles) return false;
  return user.roles.some((role) => {
    const rolePermissions = roles[role];
    if (!rolePermissions) {
      return false;
    }
    const resourcePermissions = rolePermissions[resource];
    if (!resourcePermissions) {
      return false;
    }
    const permission = resourcePermissions[action];
    if (permission == null) return false;
    if (typeof permission === "boolean" && options === undefined)
      return permission;
    if (options) {
      if (options.data) {
        return (
          options.data != null &&
          typeof permission !== "boolean" &&
          permission(user, options.data)
        );
      }
      // `isInternal` / `isExternal` / `isAutonubil` only *narrow* an already-granted
      // permission by the user's organization attributes - they must never *grant* a
      // permission that the role's table entry denies. Requiring `permission === true`
      // here closes that widening hole (a `false`/function entry can no longer be
      // satisfied via the org check alone). "Internal" is attribute-based
      // (isMedocino || isAutonubil), shared with events/opsanio (AUTH_RECOMMENDATION.md §3).
      //
      // The org narrows are a DISJUNCTION: an option set may request more than one
      // class at once - `{ isInternal: true, isExternal: true }` means "internal OR
      // external" = anyone who already holds the role grant (the opsanio catalogs use
      // this idiom for fields both staff and customers may edit). A user passes when
      // they match ANY requested class. Evaluating them as an OR (rather than the first
      // matching branch) is what lets internal users through a both-flags field while
      // still never widening past `permission === true`.
      const flags = getOrgAttributeFlags(user);
      if (options.isExternal || options.isInternal || options.isAutonubil) {
        if (permission !== true) return false;
        return (
          (options.isExternal === true && !flags.isInternal) ||
          (options.isInternal === true && flags.isInternal) ||
          (options.isAutonubil === true && flags.isAutonubil)
        );
      }
    }
    return false;
  });
}

export const hasRolePermission = ({
  user,
  roles,
}: {
  user: AnketaAuthUser;
  roles: string[];
}) => {
  if (!user || !user.roles) return false;
  const isAutonubilAdmin = user.roles.some(
    (role) =>
      role.includes("anketa-admin") &&
      user.organization.some((org) => org.alias === "autonubil-gmbh")
  );
  if (isAutonubilAdmin) {
    return true;
  }
  const hasRole = user.roles.some((role) => roles.includes(role));

  return hasRole;
};
